/**
 * Design System - Color Tokens
 *
 * Color palette extracted from Figma design
 * Following naming convention: {category}/{variant}/{shade}
 */

export const colors = {
  // Brand Colors
  brand: {
    primary: '#FDE047', // Yellow - Main brand color
    secondary: '#F6EBB5', // Light Yellow
    tertiary: '#FBF8EA', // Very Light Yellow
    offset: '#FEFCE8', // Yellow 50 - Lightest yellow background
  },

  // Semantic Colors
  background: {
    primary: '#FFFFFF', // White
    secondary: '#F5F5F4', // Stone 100
    offset: '#F5F5F4', // Stone 100
    shade: '#E7E5E4', // Stone 200
    alternate: '#1C1917', // Stone 900 - Dark
    foodSpecialty: '#FFFBF5', // Cream - Food specialty card background
  },

  typography: {
    primary: '#1C1917', // Stone 900 - Main text
    secondary: '#57534E', // Stone 600 - Secondary text
    tertiary: '#78716C', // Stone 500 - Tertiary text
    placeholder: '#78716C', // Stone 500 - Placeholder text
    alternate: '#FFFFFF', // White - Text on dark backgrounds
    text: '#44403C', // Stone 700 - Body text
    'alt-offset': '#E7E5E4', // Stone 200 - Alternative offset text
  },

  stroke: {
    lightest: '#F5F5F4', // Stone 100
    light: '#E7E5E4', // Stone 200
    base: '#D6D3D1', // Stone 300
    medium: '#A8A29E', // Stone 400
    brand: '#FEF08A', // Yellow 200 - Brand border color
    foodSpecialty: '#FFFCED', // Light cream - Food specialty card border
  },

  // Status Colors
  status: {
    success: {
      light: '#DCF5E6', // Light green background
      border: '#BFE5CD', // Green border
      text: '#16A34A', // Green 600
      icon: '#84CC16', // Lime 500
    },
    error: {
      light: '#FEE2E2', // Red 100
      border: '#FECACA', // Red 200
      text: '#DC2626', // Red 600
    },
    warning: {
      light: '#FEF3C7', // Amber 100
      border: '#FDE68A', // Amber 200
      text: '#D97706', // Amber 600
    },
    info: {
      light: '#DBEAFE', // Blue 100
      border: '#BFDBFE', // Blue 200
      text: '#2563EB', // Blue 600
    },
  },

  // Grayscale
  gray: {
    50: '#FAFAF9',
    100: '#F5F5F4',
    200: '#E7E5E4',
    300: '#D6D3D1',
    400: '#A8A29E',
    500: '#78716C',
    600: '#57534E',
    700: '#44403C',
    800: '#292524',
    900: '#1C1917',
  },

  // Overlay & Shadow Colors
  overlay: {
    light: 'rgba(0, 0, 0, 0.1)',
    medium: 'rgba(0, 0, 0, 0.2)',
    dark: 'rgba(0, 0, 0, 0.5)',
    darker: 'rgba(0, 0, 0, 0.8)',
  },

  // Additional UI Colors
  ui: {
    divider: '#E7E5E4',
    hover: 'rgba(0, 0, 0, 0.05)',
    pressed: 'rgba(0, 0, 0, 0.1)',
    focus: '#FDE047',
    disabled: '#D6D3D1',
  },

  // Base colors for direct use
  base: {
    white: '#FFFFFF',
    black: '#000000',
    transparent: 'transparent',
  },
} as const;

export type ColorToken = typeof colors;

// Color utility function to get nested color values
export const getColor = (path: string): string => {
  const keys = path.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let value: any = colors;

  for (const key of keys) {
    value = value?.[key];
  }

  return typeof value === 'string' ? value : '';
};

// Export individual color groups for convenience
export const brandColors = colors.brand;
export const backgroundColors = colors.background;
export const typographyColors = colors.typography;
export const strokeColors = colors.stroke;
export const statusColors = colors.status;
export const grayColors = colors.gray;
export const overlayColors = colors.overlay;
